import { Button, Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { get } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
// eslint-disable-next-line max-len
import DealershipTranslatedTextAreaField from '../../../../components/fields/DealershipFields/DealershipTranslatedTextArea';
// eslint-disable-next-line max-len
import DealershipTranslationTextField from '../../../../components/fields/DealershipFields/DealershipTranslationTextField';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';

type FinderApplicationModuleEmailFormProps = {
    prefix?: string;
    showUpdateButton?: boolean;
    reservationPeriod: number;
    targetDealerId?: string;
    companyId: string;
    defaultValueDisabled: boolean;
    disabled?: boolean;
};

const fieldNames = [
    'submitOrder.subject',
    'submitOrder.introTitle',
    'submitOrder.contentText',
    'submitOrder.isSummaryVehicleVisible',
    'submitOrder.introImage',
    'reminderEmail.subject',
    'reminderEmail.introTitle',
    'reminderEmail.contentText',
    'reminderEmail.sendingTime',
    'reminderEmail.active',
    'reminderEmail.introImage',
] as const;

const FinderApplicationModuleEmailForm = ({
    prefix,
    showUpdateButton = true,
    reservationPeriod,
    targetDealerId,
    companyId,
    defaultValueDisabled,
    disabled,
}: FinderApplicationModuleEmailFormProps) => {
    const { t } = useTranslation('finderApplicationModuleDetails');

    const { yesNoSwitch } = useSystemSwitchData();

    const { values } = useFormikContext();

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    const { dealerIdsByCompanyId } = useMultipleDealerIds();

    const dealerIds = useMemo(
        () => (targetDealerId ? [targetDealerId] : dealerIdsByCompanyId(companyId)),
        [companyId, dealerIdsByCompanyId, targetDealerId]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('finderApplicationModuleDetails:subTitles.emails.submitOrder')}>
                <Panel
                    key={t('finderApplicationModuleDetails:subTitles.emails.submitOrder')}
                    extra={
                        showUpdateButton &&
                        !disabled && (
                            <Button form="updateEmailForm" htmlType="submit" type="primary">
                                {t('finderApplicationModuleDetails:mainActionButtons.update')}
                            </Button>
                        )
                    }
                    header={t('finderApplicationModuleDetails:subTitles.emails.submitOrder')}
                >
                    <Row gutter={10}>
                        <Col lg={8} xs={24}>
                            <DealershipTranslationTextField
                                {...t('finderApplicationModuleDetails:fields.submitOrder.subject', {
                                    returnObjects: true,
                                })}
                                dealerIds={dealerIds}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['submitOrder.subject']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslationTextField
                                {...t('finderApplicationModuleDetails:fields.submitOrder.introTitle', {
                                    returnObjects: true,
                                })}
                                dealerIds={dealerIds}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['submitOrder.introTitle']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslatedTextAreaField
                                {...t('finderApplicationModuleDetails:fields.submitOrder.contentText', {
                                    returnObjects: true,
                                })}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['submitOrder.contentText']}
                                targetDealerId={targetDealerId}
                                withContentRefinement
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <FormFields.SwitchField
                                {...yesNoSwitch}
                                {...t('finderApplicationModuleDetails:fields.submitOrder.isSummaryVehicleVisible', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={names['submitOrder.isSummaryVehicleVisible']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <SingleUploadField
                                extensions={allowedExtensions.image}
                                {...t('finderApplicationModuleDetails:fields.submitOrder.introImage', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={names['submitOrder.introImage']}
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            {reservationPeriod !== 0 && (
                <CollapsibleWrapper
                    defaultActiveKey={t('finderApplicationModuleDetails:subTitles.emails.reminderEmail')}
                >
                    <Panel
                        key={t('finderApplicationModuleDetails:subTitles.emails.reminderEmail')}
                        extra={
                            showUpdateButton &&
                            !disabled && (
                                <Button form="updateEmailForm" htmlType="submit" type="primary">
                                    {t('finderApplicationModuleDetails:mainActionButtons.update')}
                                </Button>
                            )
                        }
                        header={t('finderApplicationModuleDetails:subTitles.emails.reminderEmail')}
                    >
                        <Row gutter={10}>
                            <Col lg={8} xs={24}>
                                <FormFields.SwitchField
                                    {...yesNoSwitch}
                                    {...t('finderApplicationModuleDetails:fields.reminderEmail.active', {
                                        returnObjects: true,
                                    })}
                                    disabled={disabled}
                                    name={names['reminderEmail.active']}
                                />
                            </Col>
                            {get(names['reminderEmail.active'], values) && (
                                <>
                                    <Col lg={8} xs={24}>
                                        <DealershipTranslationTextField
                                            {...t('finderApplicationModuleDetails:fields.reminderEmail.subject', {
                                                returnObjects: true,
                                            })}
                                            dealerIds={dealerIds}
                                            defaultValueDisabled={defaultValueDisabled}
                                            disabled={disabled}
                                            name={names['reminderEmail.subject']}
                                        />
                                    </Col>
                                    <Col lg={8} xs={24}>
                                        <DealershipTranslationTextField
                                            {...t('finderApplicationModuleDetails:fields.reminderEmail.introTitle', {
                                                returnObjects: true,
                                            })}
                                            dealerIds={dealerIds}
                                            defaultValueDisabled={defaultValueDisabled}
                                            disabled={disabled}
                                            name={names['reminderEmail.introTitle']}
                                        />
                                    </Col>
                                    <Col lg={8} xs={24}>
                                        <DealershipTranslatedTextAreaField
                                            {...t('finderApplicationModuleDetails:fields.reminderEmail.contentText', {
                                                returnObjects: true,
                                            })}
                                            defaultValueDisabled={defaultValueDisabled}
                                            disabled={disabled}
                                            name={names['reminderEmail.contentText']}
                                            targetDealerId={targetDealerId}
                                            withContentRefinement
                                        />
                                    </Col>
                                    <Col lg={8} xs={24}>
                                        <FormFields.DatePickerField
                                            {...t('finderApplicationModuleDetails:fields.reminderEmail.sendingTime', {
                                                returnObjects: true,
                                            })}
                                            disabled={disabled}
                                            format={t('common:formats.timePicker')}
                                            name={names['reminderEmail.sendingTime']}
                                            picker="time"
                                            required
                                        />
                                    </Col>
                                    <Col lg={8} xs={24}>
                                        <SingleUploadField
                                            disabled={disabled}
                                            extensions={allowedExtensions.image}
                                            {...t('finderApplicationModuleDetails:fields.reminderEmail.introImage', {
                                                returnObjects: true,
                                            })}
                                            name={names['reminderEmail.introImage']}
                                        />
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Panel>
                </CollapsibleWrapper>
            )}
        </Space>
    );
};

export default FinderApplicationModuleEmailForm;
